/**
 * EVERY THRESHOLD, IN ONE FILE.
 *
 * The brief's rule: never invent a number, escalate every threshold with a researched
 * recommendation, and ship them as named constants in one file so a founder decision is one
 * edit rather than a hunt. This is that file. Nothing else in `src/transforms/clean/**`,
 * `src/qc/**` or `src/diff/**` contains a bare numeric threshold.
 *
 * Every constant below carries its SOURCE. Where the guides disagree, the disagreement is
 * recorded next to the value rather than silently resolved.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * SOURCES (fetched 2026-08-10)
 *
 *  N-GEN  Netflix, "Timed Text Style Guide: General Requirements" (doc updated 2022-10-07):
 *         minimum duration 5/6 second, maximum 7 seconds, maximum 2 lines.
 *  N-ENG  Netflix, "English (USA) Timed Text Style Guide": 42 characters per line;
 *         adult 20 cps, children's 17 cps; maximum two lines.
 *  N-ZHS  Netflix, "Chinese (Simplified) Timed Text Style Guide": 16 characters per line
 *         (SDH 18); adult 9 cps, children's 7 cps (SDH 11 / 9); maximum two lines.
 *  N-JPN  Netflix, "Japanese Timed Text Style Guide": 13 FULL-WIDTH characters per line
 *         horizontal (SDH 16, vertical 11); 4 cps regular, 7 cps SDH; minimum duration
 *         500 ms. "Full-width character, space and punctuation counts as 1 character;
 *         half-width counts as 0.5."
 *  N-KOR  Netflix, "Korean Timed Text Style Guide": 16 characters per line (Latin, spaces
 *         and punctuation count as 0.5); adult 12 cps, children's 9; SDH 14 / 11.
 *  N-TIM  Netflix, "Timed Text Style Guide: Subtitle Timing Guidelines": minimum 2 frames
 *         between subtitles at any frame rate; at 24 fps, gaps of 3–11 frames are closed to 2.
 *  N-LB   Netflix line treatment, repeated across the per-language guides: prefer a
 *         BOTTOM-HEAVY pyramid (top line shorter) for bottom-positioned subtitles, and a
 *         top-heavy one for top-positioned subtitles; break after punctuation, before
 *         conjunctions, before prepositions.
 *  BBC    BBC Subtitle Guidelines, as summarised by Clevercast and Broadcast Writer:
 *         160–180 wpm, ~37 characters per line for UK broadcast, break at natural points,
 *         and — DISAGREEING WITH NETFLIX — the LONGER line on top.
 *  AMZN   Amazon Prime Video subtitle requirements (Gotham Lab / Ekitai summaries):
 *         42 characters per line, 20 cps, 2 lines, minimum duration 1 second, maximum 7.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * TIME IS TICKS. 120,000 per second, integer, always. Never milliseconds, never floats.
 */

import type {
  CjkOptions,
  LineBreakOptions,
  QcProfile,
  TickDelta,
  Ticks,
} from '../../../contracts/cue-model.ts';
import { T, TICKS_PER_SECOND } from '../../core/time/ticks.ts';

/** Re-exported, never redefined: 120,000 ticks per second is the §2 lattice and has one home. */
export { TICKS_PER_SECOND };

const secs = (n: number): TickDelta => T.delta(Math.round(n * TICKS_PER_SECOND));

/*───────────────────────────────────────────────────────────────────────────────────────
  QC / reading-speed profiles                                          DECISION_NEEDED · 1
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * `maxCells` is in GRAPHEME CELLS (full-width = 2), never characters — the contract fixes
 * that and `Measure` implements it. Converting a guide's number therefore means knowing what
 * the guide counts:
 *
 *   Netflix EN  42 "characters", latin, 1 cell each          → 42 cells
 *   Netflix ZH  16 "characters", full-width, 2 cells each     → 32 cells
 *   Netflix JA  13 "full-width characters", half-width = 0.5  → 26 cells (the scale is
 *               exactly ours halved, so the conversion is ×2 with no rounding)
 *   Netflix KO  16 "characters", Latin counts 0.5             → 32 cells
 *   BBC         37 latin characters                           → 37 cells
 *
 * That correspondence is the reason `CjkOptions` needs no separate CJK cell limit: 42 latin
 * cells and 32 CJK cells are the same quantity measured the same way.
 */

/** Minimum duration. N-GEN 5/6 s. AMZN uses 1 s; N-JPN uses 500 ms. */
export const MIN_DURATION_TICKS: TickDelta = T.delta(100_000); // 5/6 s exactly on the lattice
/** N-GEN, AMZN and Disney+ all agree on 7 s. */
export const MAX_DURATION_TICKS: TickDelta = secs(7);

/**
 * N-TIM: 2 frames at ANY frame rate. Expressed in ticks it is rate-dependent —
 * 10,000 at 24 fps, 9,600 at 25, 8,000 at 30 — so the default takes the LARGEST (24 fps),
 * which is the conservative direction: a gap that satisfies 24 fps satisfies the rest.
 *
 * In practice min-gap IS expressed in frames by every broadcast guide and in milliseconds by
 * every consumer tool. `QcProfile.minGapTicks` is ticks, so the frame form is the caller's
 * conversion; `minGapFramesTicks()` is that conversion, in one place.
 */
export const MIN_GAP_TICKS: TickDelta = T.delta(10_000); // 2 frames @ 24 fps = 83.33 ms

/** 2 frames at an arbitrary rate, for a caller that knows the document's framerate. */
export function minGapFramesTicks(frames: number, fps: { num: number; den: number }): TickDelta {
  return T.delta(Math.round((frames * TICKS_PER_SECOND * fps.den) / fps.num));
}

/**
 * The 'too-slow' floor, and the DEFAULT for `QcProfile.minCps` — which CONTRACT 1.4.0 (S10 C5)
 * added, so `QcFlag: 'too-slow'` can finally fire. Until then the flag was declared with nothing
 * to compare against, and this constant stood in for the missing field.
 *
 * 5 cps is roughly 60 wpm — half the slowest reading speed any guide recommends — so a cue
 * below it is lingering rather than merely relaxed.
 */
export const MIN_CPS = 5;
/** The CJK counterpart: their target speeds are ~half the latin ones, so the floor halves too. */
export const MIN_CPS_CJK = 2.5;

/** The latin default — the profile a document with no language tag gets. N-ENG + N-GEN. */
export const PROFILE_LATIN: QcProfile = {
  targetCps: 17, // N-ENG children's / most-languages adult; the value most guides share
  maxCps: 20, // N-ENG adult, AMZN
  minDurationTicks: MIN_DURATION_TICKS,
  maxDurationTicks: MAX_DURATION_TICKS,
  maxCells: 42, // N-ENG, AMZN
  maxLines: 2, // N-GEN, N-ENG, N-ZHS, N-KOR, N-JPN, AMZN — universal
  minGapTicks: MIN_GAP_TICKS,
  // Contract 1.4.0 (S10 C5) gave QcProfile a minCps, so the constant CLEAN already had
  // finally has a home and 'too-slow' can fire.
  minCps: MIN_CPS,
  countSpaces: true,
};

/** BBC/UK broadcast. Same speeds, narrower line. */
export const PROFILE_LATIN_BROADCAST: QcProfile = { ...PROFILE_LATIN, maxCells: 37 };

/** Chinese. N-ZHS: 16 chars (32 cells), 9 cps adult, 11 SDH. */
export const PROFILE_CHINESE: QcProfile = {
  targetCps: 9,
  maxCps: 11,
  minDurationTicks: MIN_DURATION_TICKS,
  maxDurationTicks: MAX_DURATION_TICKS,
  maxCells: 32,
  maxLines: 2,
  minGapTicks: MIN_GAP_TICKS,
  minCps: MIN_CPS_CJK,
  countSpaces: false,
};

/**
 * Japanese. N-JPN: 13 full-width per line (26 cells), 4 cps regular / 7 cps SDH, 500 ms min.
 *
 * 4 cps is the strictest number in the entire set and it is a BROADCAST-SUBTITLE number, not
 * a caption-file number: at 4 cps a 13-character line needs 3.25 s. Shipping it as the
 * default would flag most of the Japanese files a user brings us. The recommendation is
 * therefore the SDH value, 7 cps, with 4 available as a preset — and that is a product call,
 * not mine.
 */
export const PROFILE_JAPANESE: QcProfile = {
  targetCps: 7, // N-JPN SDH; N-JPN regular is 4 — see the note above
  maxCps: 9,
  minDurationTicks: T.delta(60_000), // N-JPN 500 ms
  maxDurationTicks: MAX_DURATION_TICKS,
  maxCells: 26,
  maxLines: 2,
  minGapTicks: MIN_GAP_TICKS,
  minCps: MIN_CPS_CJK,
  countSpaces: false,
};

/** Korean. N-KOR: 16 chars (32 cells), 12 cps adult, 14 SDH. */
export const PROFILE_KOREAN: QcProfile = {
  targetCps: 12,
  maxCps: 14,
  minDurationTicks: MIN_DURATION_TICKS,
  maxDurationTicks: MAX_DURATION_TICKS,
  maxCells: 32,
  maxLines: 2,
  minGapTicks: MIN_GAP_TICKS,
  minCps: MIN_CPS_CJK,
  countSpaces: false,
};

/** Children's programming — N-ENG 17 cps, N-ZHS 7, N-KOR 9. Offered as a preset. */
export const PROFILE_LATIN_CHILDREN: QcProfile = { ...PROFILE_LATIN, targetCps: 14, maxCps: 17 };

export const NAMED_PROFILES = {
  latin: PROFILE_LATIN,
  'latin-broadcast': PROFILE_LATIN_BROADCAST,
  'latin-children': PROFILE_LATIN_CHILDREN,
  chinese: PROFILE_CHINESE,
  japanese: PROFILE_JAPANESE,
  korean: PROFILE_KOREAN,
} as const satisfies Readonly<Record<string, QcProfile>>;

export type NamedProfile = keyof typeof NAMED_PROFILES;

/**
 * BCP-47 primary subtag → the profile to use when a Track declares a language and the caller
 * supplied no per-track profile. Deliberately small: a wrong guess here is worse than the
 * latin default, which is at least predictable.
 */
const PROFILE_BY_LANGUAGE: Readonly<Record<string, NamedProfile>> = {
  zh: 'chinese',
  yue: 'chinese',
  ja: 'japanese',
  ko: 'korean',
};

/** The profile a track's declared language implies, or null when we have no opinion. */
export function profileForLanguage(language: string | null): QcProfile | null {
  if (language === null) return null;
  const primary = language.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  const named = PROFILE_BY_LANGUAGE[primary];
  return named === undefined ? null : NAMED_PROFILES[named];
}

/**
 * The floor used for 'too-slow'. Now simply the profile's own field — contract 1.4.0 (S10 C5)
 * added it. This used to INFER the band from maxCps, which was right for the four profiles
 * shipped here and wrong for any profile a user tuned themselves.
 *
 * null means the check is off for this profile, which is not the same as 0.
 */
export function minCpsFor(profile: QcProfile): number | null {
  return profile.minCps;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Line breaking                                                        DECISION_NEEDED · 2
  Escalated as ONE set, because CONVERT's resegment consumes these same constants.
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * Kinsoku character classes.
 *
 * `CjkOptions` carries two strings — "may not START a line" and "may not END a line" — which
 * is the UAX #14 CL/CP/EX/IS/NS classes on one side and OP on the other, flattened. That is
 * enough for subtitle-width text: the classes UAX #14 adds beyond these (line-break
 * opportunities inside Hangul, Brahmic clusters, hyphenation) matter for justified body text
 * and not for a 42-cell caption line. Reported as SUFFICIENT rather than requiring a contract
 * change — see comms/CLEAN.md for the reasoning and the one case that is not covered.
 */
export const CJK_NO_BREAK_BEFORE =
  '、。，．・：；？！ー〜～ゝゞヽヾ々‐゠–—”〕〉》」』】〙〗〟’｠»ヽヾー"' +
  'ぁぃぅぇぉっゃゅょゎァィゥェォッャュョヮヵヶ)]｝〕〉》」』】〗〙｣、〕)]｝，．' +
  '‥…‼⁇⁈⁉％＋℃、。';

export const CJK_NO_BREAK_AFTER = '「『（〔［｛〈《【〖〘〝‘“｟«([{（〔［｛￥＄£＃＠§';

export const CJK_DEFAULTS: CjkOptions = {
  enabled: true,
  noBreakBefore: CJK_NO_BREAK_BEFORE,
  noBreakAfter: CJK_NO_BREAK_AFTER,
};

/**
 * Ranked break preferences, strongest first.
 *
 * THE CONVENTION, carried by the shared implementation so it cannot drift: an entry that
 * begins with a space means "prefer a break BEFORE this word"; every other entry means
 * "prefer a break AFTER this string". Netflix's line-treatment rule is literally "break after
 * punctuation, before conjunctions, before prepositions", and the second half of that is not
 * expressible as a break-after string. `breakOffsets` implements both readings, and CONVERT
 * imports `breakOffsets` rather than re-reading this list, so there is one interpretation.
 *
 * Rank is the index: earlier entries outrank later ones when both are available.
 */
export const CLAUSE_BREAKERS: readonly string[] = [
  // Sentence-final punctuation — always the best break.
  '. ',
  '! ',
  '? ',
  '… ',
  '。',
  '！',
  '？',
  '…',
  // Strong internal punctuation.
  '; ',
  ': ',
  '；',
  '：',
  // Dashes, em/en/hyphen, with the space that separates them from the next word.
  ' — ',
  ' – ',
  ' - ',
  // Commas.
  ', ',
  '、',
  '，',
  // Closing punctuation that ends a unit.
  ') ',
  '] ',
  '" ',
  '” ',
  // Break BEFORE a conjunction (leading space = the "before" reading).
  ' and',
  ' but',
  ' or',
  ' nor',
  ' so',
  ' yet',
  ' because',
  ' although',
  ' while',
  ' when',
  ' where',
  ' which',
  ' who',
  ' that',
  ' if',
  // Break BEFORE a preposition.
  ' of',
  ' to',
  ' in',
  ' on',
  ' at',
  ' for',
  ' with',
  ' from',
  ' into',
  ' about',
  ' over',
  ' under',
  ' after',
  ' before',
];

/**
 * The shared line-break defaults. `maxCells` 42 and `maxLines` 2 are the near-universal
 * figures (N-ENG, AMZN, N-GEN); `balanced` is the recommendation because greedy produces the
 * one-word orphan line every guide tells editors to avoid.
 */
export const LINE_BREAK_DEFAULTS: LineBreakOptions = {
  maxCells: 42,
  maxLines: 2,
  algorithm: 'balanced',
  breakOnClause: true,
  clauseBreakers: CLAUSE_BREAKERS,
  cjk: CJK_DEFAULTS,
};

/** CJK line-break defaults — the same options with the CJK cell limit. */
export const LINE_BREAK_DEFAULTS_CJK: LineBreakOptions = { ...LINE_BREAK_DEFAULTS, maxCells: 32 };

/**
 * Which line should be shorter. THE conventions disagree, so this is a named constant rather
 * than a hardcoded comparison:
 *
 *   'bottom-heavy'  top line shorter — Netflix, for bottom-positioned subtitles (N-LB)
 *   'top-heavy'     top line longer  — BBC's pyramid, and Netflix for TOP-positioned cues
 *
 * The default follows Netflix because bottom-positioned is the overwhelmingly common case.
 * `ReflowOp` flips it to 'top-heavy' for a cue whose resolved alignment is a top alignment
 * (7/8/9), which is what both guides actually say once you read them together.
 */
export const LINE_SHAPE: 'bottom-heavy' | 'top-heavy' = 'bottom-heavy';

/**
 * Re-segmentation defaults, escalated in the SAME set because CONVERT's `ResegmentOp` embeds
 * `LineBreakOptions` and its duration bounds have to agree with the QC profile that will
 * later judge its output. 1 s / 7 s are the contract's own comments; `maxCps` is N-ENG adult;
 * 800 ms silence split is the value the contract names.
 */
export const RESEGMENT_DEFAULTS = {
  minDurationTicks: secs(1),
  maxDurationTicks: secs(7),
  maxCps: 20,
  minGapTicks: MIN_GAP_TICKS,
  silenceSplitTicks: secs(0.8),
  padStartTicks: T.delta(7_200), // 60 ms — ASR onsets run late
  padEndTicks: T.delta(14_400), // 120 ms
} as const;

/*───────────────────────────────────────────────────────────────────────────────────────
  Cleaning op defaults                                                 DECISION_NEEDED · 3
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * `MergeSameTextOp.threshold` — the gap below which two identical adjacent cues are one cue
 * that a renderer blinked. 200 ms is longer than any legitimate re-cue of the same line and
 * shorter than the pause that would make a repeat deliberate (a chanted refrain).
 */
export const MERGE_SAME_TEXT_THRESHOLD: TickDelta = T.delta(24_000); // 200 ms

/** `NormalizeOp` gives a degenerate cue (end <= start) this much duration rather than dropping it. */
export const NORMALIZE_MIN_SPAN_TICKS: TickDelta = T.delta(1);

/**
 * `CaseOp.protectAcronyms` — "leave all-caps tokens ≤ 4 chars alone".
 *
 * 4 is a defensible cut (NASA, NATO, WHO, USA, FBI, TV, UK all fit; ASCII, UNESCO, UNICEF do
 * not) but the LOWER bound is the one that actually breaks: with no floor, "THIS IS A TEST"
 * sentence-cases to "This is A test", because 'A' is an all-caps token of length ≤ 4. So the
 * rule is a RANGE plus an exception list of English words that are commonly all-caps in
 * shouty source text and are almost never acronyms in a caption.
 *
 * The residual ambiguity is real and unfixable by list: US, IT, WHO and MAY are genuinely
 * both. The list resolves each one in the direction that damages the sentence less.
 */
export const ACRONYM_MIN_LENGTH = 2;
export const ACRONYM_MAX_LENGTH = 4;

/**
 * AND THE FINDING THAT MATTERS MORE THAN THE CUT: "leave all-caps tokens alone" carries NO
 * INFORMATION in an all-caps file, which is the exact input sentence-case exists for. In
 * 'THIS IS THE FIRST LINE.', the tokens THIS, LINE, IS and THE are all-caps tokens of ≤ 4
 * characters, and protecting them produces 'This is the first LINE.' — visibly worse than
 * doing nothing.
 *
 * So the rule is conditional on the input:
 *   MIXED-CASE text  an all-caps token genuinely stands out → the length rule applies
 *   ALL-CAPS text    nothing stands out → only UNAMBIGUOUS acronyms are protected: dotted
 *                    initialisms (U.S., J.R.R.), tokens containing a digit (MP3, H2O), and
 *                    this list
 *
 * The list is English and it is data, not a rule — there is no rule. DECISION_NEEDED on its
 * contents; it is the kind of list that grows from support tickets.
 */
export const KNOWN_ACRONYMS: readonly string[] = [
  'AI',
  'AM',
  'PM',
  'BC',
  'AD',
  'CD',
  'DVD',
  'TV',
  'PC',
  'OS',
  'IP',
  'URL',
  'HTML',
  'CSS',
  'API',
  'SQL',
  'PDF',
  'GIF',
  'JPEG',
  'USB',
  'GPS',
  'PIN',
  'ATM',
  'CEO',
  'CFO',
  'CTO',
  'FAQ',
  'DNA',
  'RNA',
  'HIV',
  'AIDS',
  'ICU',
  'EMT',
  'ER',
  'MRI',
  'CPR',
  'UFO',
  'LED',
  'LCD',
  'NASA',
  'FBI',
  'CIA',
  'NSA',
  'USA',
  'UK',
  'US',
  'EU',
  'UN',
  'NATO',
  'WHO',
  'IMF',
  'NGO',
  'BBC',
  'CNN',
  'NBC',
  'ABC',
  'CBS',
  'HBO',
  'MTV',
  'NPR',
  'PBS',
  'NBA',
  'NFL',
  'MLB',
  'NHL',
  'FIFA',
  'UFC',
  'MVP',
  'DJ',
  'MC',
  'VIP',
  'LOL',
  'OMG',
  'ASAP',
  'RIP',
  'SOS',
  'FYI',
  'DIY',
  'IQ',
  'ID',
  'OK',
];
export const ACRONYM_EXCEPTIONS: readonly string[] = [
  'A',
  'I',
  'AN',
  'AS',
  'AT',
  'BE',
  'BY',
  'DO',
  'GO',
  'HE',
  'IF',
  'IN',
  'IS',
  'IT',
  'ME',
  'MY',
  'NO',
  'OF',
  'ON',
  'OR',
  'SO',
  'TO',
  'UP',
  'WE',
  'ALL',
  'AND',
  'ANY',
  'ARE',
  'BUT',
  'CAN',
  'FOR',
  'GET',
  'HAD',
  'HAS',
  'HER',
  'HIM',
  'HIS',
  'HOW',
  'ITS',
  'NOT',
  'NOW',
  'ONE',
  'OUR',
  'OUT',
  'SEE',
  'SHE',
  'THE',
  'TOO',
  'TWO',
  'WAS',
  'WAY',
  'WHY',
  'YES',
  'YET',
  'YOU',
  'BEEN',
  'DOES',
  'DONE',
  'EACH',
  'ELSE',
  'EVEN',
  'EVER',
  'FROM',
  'HAVE',
  'HERE',
  'JUST',
  'KNOW',
  'LIKE',
  'MAKE',
  'MORE',
  'MOST',
  'MUCH',
  'MUST',
  'NEED',
  'ONLY',
  'OVER',
  'SAID',
  'SAME',
  'SOME',
  'SUCH',
  'TAKE',
  'THAN',
  'THAT',
  'THEM',
  'THEN',
  'THEY',
  'THIS',
  'TIME',
  'VERY',
  'WANT',
  'WELL',
  'WENT',
  'WERE',
  'WHAT',
  'WHEN',
  'WILL',
  'WITH',
  'YOUR',
];

/**
 * Sentence terminators. `CaseOp.sentenceTerminators` is data because '3.5 million' and
 * 'Mr. Smith' break without the abbreviation table below, and because CJK terminators are a
 * different set entirely — inert where the script has no case, live in a mixed-script line.
 */
export const SENTENCE_TERMINATORS: readonly string[] = [
  '.',
  '!',
  '?',
  '。',
  '！',
  '？',
  '‼',
  '⁇',
  '⁈',
  '⁉',
];

/**
 * '…' and '...' are DELIBERATELY ABSENT from the terminator list.
 *
 * In subtitling an ellipsis at the end of a cue is the standard marker for "this sentence
 * continues in the next cue", and mid-line it marks a trailing-off rather than a stop. Every
 * guide uses it that way. Treating it as a terminator is the single most common way an
 * automatic sentence-caser produces the broken output the competitor ships:
 * 'I was going to…' / 'Tell him.' becomes 'I was going to…' / 'Tell him.' either way, but
 * 'I don't know…' / 'what he wants' becomes 'What he wants', which is wrong.
 */
export const ELLIPSIS_CONTINUES = true;

/**
 * Sentence case on ALREADY MIXED-CASE text must not lowercase proper nouns, and sentence case
 * on an ALL-CAPS file must. So the op decides once, per pass, whether its input is shouting:
 * at least this many cased letters, and at least this fraction of them uppercase.
 *
 * The decision is taken over the whole SELECTION rather than per cue, because a file is
 * either an all-caps file or it is not, and a per-cue decision produces a document whose
 * casing rule changes halfway down.
 */
export const SHOUT_MIN_LETTERS = 12;
export const SHOUT_RATIO = 0.6;

/**
 * Title-case minor words — never capitalised unless they are the first or last word of a
 * line. English only; title case is barely meaningful outside it, which is itself worth
 * saying to the user rather than pretending otherwise.
 */
export const TITLE_MINOR_WORDS: readonly string[] = [
  'a',
  'an',
  'and',
  'as',
  'at',
  'but',
  'by',
  'for',
  'from',
  'if',
  'in',
  'into',
  'nor',
  'of',
  'off',
  'on',
  'onto',
  'or',
  'over',
  'per',
  'so',
  'the',
  'to',
  'up',
  'via',
  'vs',
  'with',
  'yet',
];

/**
 * Abbreviations whose trailing '.' never ends a sentence.
 *
 * HOW BIG DOES IT NEED TO BE: the false-sentence-start rate is dominated by a short head —
 * titles (Mr/Mrs/Ms/Dr/Prof/St), initialisms written with dots (U.S., U.K., a.m., p.m.,
 * e.g., i.e., etc.), and single capital letters used as initials ('J. R. Ewing'). Numbers
 * are handled structurally rather than by list: a '.' between two digits is never a
 * terminator, which covers '3.5 million', version numbers and prices in one rule and needs
 * no entries at all.
 *
 * It DOES need to be per-language — German 'z.B.', 'usw.', 'Nr.'; French 'M.', 'Mme',
 * 'cf.'; Spanish 'Sr.', 'Sra.', 'EE.UU.' — which is why the list is op DATA and not a
 * constant baked into the matcher. This is the English default.
 */
export const ABBREVIATIONS_EN: readonly string[] = [
  'Mr.',
  'Mrs.',
  'Ms.',
  'Dr.',
  'Prof.',
  'Sr.',
  'Jr.',
  'St.',
  'Mt.',
  'Ft.',
  'Rev.',
  'Hon.',
  'Gen.',
  'Col.',
  'Sgt.',
  'Capt.',
  'Lt.',
  'Cmdr.',
  'Gov.',
  'Sen.',
  'Rep.',
  'Pres.',
  'a.m.',
  'p.m.',
  'A.M.',
  'P.M.',
  'e.g.',
  'i.e.',
  'etc.',
  'vs.',
  'approx.',
  'No.',
  'no.',
  'Fig.',
  'fig.',
  'Vol.',
  'vol.',
  'p.',
  'pp.',
  'Ch.',
  'ch.',
  'Inc.',
  'Ltd.',
  'Co.',
  'Corp.',
  'Ave.',
  'Blvd.',
  'Rd.',
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'Jun.',
  'Jul.',
  'Aug.',
  'Sep.',
  'Sept.',
  'Oct.',
  'Nov.',
  'Dec.',
  'U.S.',
  'U.K.',
  'U.N.',
  'E.U.',
  'Ph.D.',
  'B.A.',
  'M.A.',
  'D.C.',
  'A.D.',
  'B.C.',
];

export const ABBREVIATIONS_DE: readonly string[] = [
  'z.B.',
  'u.a.',
  'd.h.',
  'bzw.',
  'usw.',
  'Nr.',
  'Hr.',
  'Fr.',
  'Dr.',
  'Prof.',
  'ca.',
  'evtl.',
  'ggf.',
  'inkl.',
  'max.',
  'min.',
  'Str.',
  'Abb.',
  'vgl.',
];
export const ABBREVIATIONS_FR: readonly string[] = [
  'M.',
  'MM.',
  'Mme',
  'Mlle',
  'Dr.',
  'Pr.',
  'cf.',
  'env.',
  'p.ex.',
  'c.-à-d.',
  'etc.',
  'St.',
  'Ste.',
  'av.',
  'bd.',
];
export const ABBREVIATIONS_ES: readonly string[] = [
  'Sr.',
  'Sra.',
  'Srta.',
  'Dr.',
  'Dra.',
  'Ud.',
  'Uds.',
  'etc.',
  'p.ej.',
  'EE.UU.',
  'núm.',
  'pág.',
  'av.',
];

const ABBREVIATIONS_BY_LANGUAGE: Readonly<Record<string, readonly string[]>> = {
  en: ABBREVIATIONS_EN,
  de: ABBREVIATIONS_DE,
  fr: ABBREVIATIONS_FR,
  es: ABBREVIATIONS_ES,
};

/** The abbreviation list a locale implies; English is the fallback, never an empty list. */
export function abbreviationsFor(locale: string | null): readonly string[] {
  if (locale === null) return ABBREVIATIONS_EN;
  const primary = locale.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return ABBREVIATIONS_BY_LANGUAGE[primary] ?? ABBREVIATIONS_EN;
}

/** Default joiners for UnwrapOp — a space between latin, nothing between CJK. */
export const UNWRAP_JOINER = ' ';
export const UNWRAP_CJK_JOINER = '';

/**
 * How many cues an op processes between cancellation polls. `CHUNK.CUES` from
 * `src/core/protocol.ts`; restated through this file so every threshold has one home.
 */
export { CHUNK } from '../../core/protocol.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  Media-end tolerance
───────────────────────────────────────────────────────────────────────────────────────*/

/** A cue may end this far past `meta.durationTicks` before 'past-media-end' fires. Encoders
 *  routinely round the container duration down by a frame or two. */
export const PAST_MEDIA_END_TOLERANCE: TickDelta = T.delta(10_000); // 2 frames @ 24 fps

/** Zero, as a Ticks, for the negative-time clamp. */
export const ZERO_TICKS: Ticks = T.ticks(0);
